<template>
  <v-container fluid>
    <TitleBar title="Update Employee" />

    <v-card outlined class="rounded-xl mt-5 glass-card">
      <CardToolbar title="Employee" :isModal="false" />

      <!-- Basic Info -->
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              type="text"
              v-model="employee.firstName"
              @focus="$event.target.select()"
              :error-messages="firstNameErrors"
              label="First Name"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              type="text"
              v-model="employee.lastName"
              @focus="$event.target.select()"
              label="Last Name"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              v-model="employee.pinCode"
              :append-icon="showPassword ? 'visibility_off' : 'visibility'"
              :type="showPassword ? 'text' : 'password'"
              label="Pin Code"
              @focus="$event.target.select()"
              @keyup.enter="submit"
              :error-messages="pinErrors"
              counter
              @click:append="showPassword = !showPassword"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              type="tel"
              v-model="employee.phoneNumber"
              :error-messages="phoneNumberErrors"
              label="Phone Number"
              @focus="$event.target.select()"
            />
          </v-col>
        </v-row>

        <!-- Types, TimeClock Login, Email, Note -->
        <v-row>
          <v-col cols="12" sm="6">
            <v-row>
              <v-col cols="12">
                <v-checkbox
                  v-if="locationModules.terminalordering"
                  hide-details
                  v-model="employee.metadata.isDriver"
                  label="Driver"
                />
                <v-checkbox
                  v-if="
                    locationModules.terminalrepairs ||
                    locationModules.terminalautoshop
                  "
                  hide-details
                  v-model="employee.metadata.isTechnician"
                  label="Technician"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              type="email"
              v-model="employee.email"
              :error-messages="emailErrors"
              label="Email Address"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              outlined
              label="Note"
              auto-grow
              v-model="employee.note"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Hide Payee + Payee List -->
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="9" lg="10">
            <span class="font-weight-medium">Hide Payee</span>
            <p class="font-weight-light mb-0">
              Hide selected payees from {{ employee.firstName }}
              {{ employee.lastName }}.
            </p>
          </v-col>

          <v-col cols="12" md="3" lg="2">
            <v-switch
              :loading="payeeLoading"
              v-model="hidePayee"
              color="primary"
              :label="hidePayee ? 'Enabled' : 'Disabled'"
            />
          </v-col>
        </v-row>

        <v-card flat outlined v-if="hidePayee">
          <v-card-text>
            <v-row class="mb-3" align="center">
              <v-col cols="12" sm="6">
                <span class="font-weight-medium">Payee List</span>
              </v-col>

              <v-spacer />

              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="search"
                  label="Search"
                  :append-icon="search ? 'close' : ''"
                  @click:append="setNull()"
                  single-line
                  outlined
                  hide-details
                  class="rounded"
                />
              </v-col>
            </v-row>

            <v-data-table
              v-model="selected"
              :headers="headers"
              :items="Payees"
              :search="search"
              :options.sync="tableOptions"
              item-key="id"
              show-select
              class="elevation-0 table-header"
            >
              <template v-slot:item.accountNumber="{ item }">
                <span>{{
                  item.accountNumber ? item.accountNumber : "N/A"
                }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>

      <!-- Address & Locations -->
      <v-card-text>
        <v-expansion-panels multiple class="rounded-xl">
          <!-- Address Panel -->
          <v-expansion-panel>
            <v-expansion-panel-header> Address </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-card outlined class="rounded-xl">
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        outlined
                        type="text"
                        v-model="employee.Address.type"
                        @focus="$event.target.select()"
                        label="Type"
                        hint="e.g. Home, Office, Business"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        outlined
                        type="text"
                        v-model="employee.Address.phoneNumber"
                        @focus="$event.target.select()"
                        label="Phone Number"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        outlined
                        type="text"
                        v-model="employee.Address.addressLine1"
                        :error-messages="addressLine1Errors"
                        label="Address"
                        @focus="$event.target.select()"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        outlined
                        type="text"
                        v-model="employee.Address.addressLine2"
                        @focus="$event.target.select()"
                        label="Address Line 2"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        outlined
                        type="text"
                        v-model="employee.Address.city"
                        :error-messages="cityErrors"
                        label="City"
                        @focus="$event.target.select()"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-autocomplete
                        outlined
                        :items="states"
                        label="State"
                        item-text="name"
                        item-value="id"
                        v-model="employee.Address.StateId"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        outlined
                        type="text"
                        v-model="employee.Address.postalCode"
                        label="Zip Code"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-autocomplete
                        outlined
                        :items="countries"
                        label="Country"
                        item-text="name"
                        item-value="code"
                        v-model="employee.Address.CountryCode"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea
                        outlined
                        name="input-7-1"
                        label="Description"
                        auto-grow
                        v-model="employee.Address.description"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Locations Panel -->
          <v-expansion-panel>
            <v-expansion-panel-header> Locations </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-card outlined class="rounded-xl">
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="5">
                      <v-autocomplete
                        outlined
                        label="Select Location"
                        :items="locations"
                        item-text="name"
                        v-model="selectedLocation"
                        return-object
                      >
                        <template v-slot:item="{ item }">
                          <span>{{ item.name }}</span>
                          <v-spacer />
                          <span v-if="item.Address">
                            {{ item.Address.addressLine1 }}
                          </span>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="5">
                      <v-autocomplete
                        outlined
                        label="Select Role"
                        :items="roles"
                        item-text="name"
                        v-model="selectedRole"
                        return-object
                      />
                    </v-col>

                    <v-col cols="12" md="2" class="d-flex align-center">
                      <v-btn
                        color="primary"
                        :disabled="!selectedLocation || !selectedRole"
                        @click="addLocation"
                      >
                        Add Location
                      </v-btn>
                    </v-col>

                    <v-col cols="12">
                      <v-data-table
                        :headers="locationTable.headers"
                        :items="employee.ActiveLocations"
                        hide-default-footer
                      >
                        <template v-slot:item="{ item, index }">
                          <tr v-if="!item.delete">
                            <td class="text-xs-left">
                              {{ item.Location && item.Location.name }}
                            </td>
                            <td class="text-xs-left">
                              {{
                                item.EmployeeRole
                                  ? item.EmployeeRole.name
                                  : "No Role"
                              }}
                            </td>
                            <td class="text-xs-center">
                              <v-icon @click="deleteLocation(index)">
                                delete
                              </v-icon>
                            </td>
                          </tr>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions :style="{ 'background-color': $style.card.action }">
        <v-spacer />
        <v-btn text @click="cancel"> Cancel </v-btn>
        <v-btn color="pink" dark @click="submit"> Update </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import payeeService from "@/modules/Payee/service.js";
import employeeService from "../service";
import locationService from "../../Location/service.js";
import rolesService from "../modules/Roles/service.js";

const { validationMixin } = require("vuelidate");
const {
  required,
  minLength,
  email,
  requiredIf,
} = require("vuelidate/lib/validators");

export default {
  name: "employee-edit",
  props: {
    isModal: Boolean,
  },
  data() {
    return {
      search: "",
      tableOptions: {
        sortBy: ["name"],
      },
      selected: [],
      headers: [
        {
          text: "Name",
          align: "left",
          value: "name",
        },
        {
          text: "Phone Number",
          align: "left",
          value: "phoneNumber",
        },
        {
          text: "Email",
          align: "left",
          value: "email",
        },
        {
          text: "Account",
          align: "left",
          value: "accountNumber",
        },
      ],
      payeeLoading: false,
      Payees: [],
      hidePayee: false,
      locationId: null,
      showPassword: false,
      locations: [],
      roles: [],
      selectedLocation: null,
      selectedRole: null,
      locationTable: {
        headers: [
          {
            text: "Location",
            value: "Location",
            sortable: false,
          },
          {
            text: "Role",
            value: "EmployeeRole",
            sortable: false,
          },
          {
            text: "Action",
            value: "action",
            sortable: false,
            align: "center",
          },
        ],
      },
      employee: {
        id: null,
        firstName: null,
        lastName: null,
        phoneNumber: null,
        accountNumber: null,
        pinCode: null,
        confirmPin: null,
        hourlyRate: null,
        email: null,
        website: null,
        note: null,
        Address: {},
        ActiveLocations: [],
        metadata: {
          isDriver: false,
          isTechnician: false,
          employeeHours: {
            breakHours: null,
            workingHours: null,
          },
          hidePayee: {
            list: [],
            isEnable: false,
          },
          timeClockLogin: {
            admin: false,
            employee: false,
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters("global", [
      "states",
      "countries",
      "defaultCountryCode",
      "location",
    ]),
    ...mapGetters("integration", ["locationModules", "modules"]),
    checkTimeClockModule() {
      if (
        this.locationModules &&
        this.locationModules.terminaltimeclock &&
        this.locationModules.terminaltimeclock.LocationIntegrations &&
        this.locationModules.terminaltimeclock.LocationIntegrations.length
      ) {
        return true;
      }
      return false;
    },
    Address() {
      return this.employee.Address || {};
    },
    firstNameErrors() {
      const errors = [];
      if (!this.$v.employee.firstName.$dirty) return errors;
      if (!this.$v.employee.firstName.required) {
        errors.push("This field is required.");
      }
      if (!this.$v.employee.firstName.minLength) {
        errors.push("First name must be at least 2 characters long.");
      }
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.employee.lastName.$dirty) return errors;
      if (!this.$v.employee.lastName.minLength) {
        errors.push("Last name must be at least 2 characters long.");
      }
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.employee.phoneNumber.$dirty) return errors;
      if (!this.$v.employee.phoneNumber.minLength) {
        errors.push("Phone number must be at least 10 digits.");
      }
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.employee.email.$dirty) return errors;
      if (!this.$v.employee.email.email) {
        errors.push("Email address is not valid.");
      }
      return errors;
    },
    addressLine1Errors() {
      const errors = [];
      if (!this.$v.employee.Address.addressLine1.$dirty) return errors;
      if (!this.$v.employee.Address.addressLine1.minLength) {
        errors.push("Address must be at least 2 characters.");
      }
      if (!this.$v.employee.Address.addressLine1.required) {
        errors.push("This field is required.");
      }
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.employee.Address.city.$dirty) return errors;
      if (!this.$v.employee.Address.city.required) {
        errors.push("City is required.");
      }
      return errors;
    },
    pinErrors() {
      const errors = [];
      if (!this.$v.employee.pinCode.$dirty) return errors;
      if (!this.$v.employee.pinCode.required) {
        errors.push("This field is required.");
      }
      if (!this.$v.employee.pinCode.minLength) {
        errors.push("Pin code must be at least 4 characters.");
      }
      return errors;
    },
  },
  watch: {
    hidePayee(val) {
      this.employee.metadata.hidePayee.isEnable = val;
      if (val) {
        this.getPayee();
      }
    },
    Address() {
      if (this.employee.Address && Object.keys(this.employee.Address).length) {
        if (
          !this.employee.Address.city &&
          this.location &&
          this.location.Address
        ) {
          this.employee.Address.city = this.location.Address.city;
        }
        if (
          !this.employee.Address.StateId &&
          this.location &&
          this.location.Address
        ) {
          this.employee.Address.StateId = this.location.Address.StateId;
        }
        if (
          !this.employee.Address.CountryCode &&
          this.location &&
          this.location.Address
        ) {
          this.employee.Address.CountryCode = this.location.Address.CountryCode;
        }
      }
    },
  },
  async created() {
    try {
      this.roles = (await rolesService.getAll()).data;
      this.locations = (await locationService.getAll({ limit: 40 })).data;

      if (this.$route.params && this.$route.params.LocationId) {
        this.locationId = this.$route.params.LocationId;
      }

      await this.loademployee();
    } catch (error) {
      console.log(error);
    }
  },
  methods: {
    setNull() {
      this.search = "";
    },
    getPayee() {
      this.payeeLoading = true;
      return payeeService
        .getAll({ limit: 500 })
        .then((response) => {
          this.Payees = response.data;
          this.payeeLoading = false;
          if (
            this.employee.metadata.hidePayee &&
            this.employee.metadata.hidePayee.isEnable &&
            this.employee.metadata.hidePayee.list &&
            this.employee.metadata.hidePayee.list.length > 0
          ) {
            this.employee.metadata.hidePayee.list.map((row) => {
              const found = this.Payees.find((payee) => payee.id === row);
              if (found) {
                this.selected.push(found);
              }
              return true;
            });
          }
        })
        .catch((error) => {
          console.log("error", error);
          this.payeeLoading = false;
          this.$swal("Payee", "Can't get payee list", "error");
        });
    },
    loademployee() {
      if (
        !this.$route.params ||
        (this.$route.params &&
          this.$route.params.Employee &&
          !this.$route.params.Employee.id)
      ) {
        return this.$router.go(-1);
      }
      const id = this.$route.params.Employee.id;
      this.$Progress.start();
      employeeService
        .getById(id, { includeLocations: true })
        .then((response) => {
          this.$Progress.finish();
          this.employee.id = response.data.id;
          this.employee.firstName = response.data.firstName;
          this.employee.lastName = response.data.lastName;
          this.employee.phoneNumber = response.data.phoneNumber;
          this.employee.accountNumber = response.data.accountNumber;
          this.employee.email = response.data.email;
          this.employee.website = response.data.website;
          this.employee.note = response.data.note;
          this.employee.hourlyRate = response.data.hourlyRate;
          this.employee.pinCode = response.data.pinCode;

          if (response.data.metadata) {
            this.employee.metadata = response.data.metadata;

            if (!this.employee.metadata.employeeHours) {
              this.employee.metadata.employeeHours = {
                breakHours: null,
                workingHours: null,
              };
            }
            if (!this.employee.metadata.timeClockLogin) {
              this.employee.metadata.timeClockLogin = {
                admin: false,
                employee: false,
              };
            }
            if (!this.employee.metadata.hidePayee) {
              this.employee.metadata.hidePayee = {
                list: [],
                isEnable: false,
              };
            } else {
              this.hidePayee = this.employee.metadata.hidePayee.isEnable;
            }
          }

          this.employee.ActiveLocations = [];
          Promise.all(
            response.data.ActiveLocations.map(async (location) => {
              this.selectedLocation = location;
              await this.addLocation();
              return true;
            })
          );

          this.employee.Address = response.data.Address || {};
        })
        .catch((error) => {
          this.$Progress.fail();
          console.log(error);
        });
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            timer: 2000,
          })
          .fire({
            type: "error",
            title: "Please fix the highlighted fields.",
          });
      } else {
        this.$Progress.start();

        this.employee.metadata.hidePayee.list = [];
        if (this.selected && this.selected.length > 0) {
          this.selected.map((row) => {
            this.employee.metadata.hidePayee.list.push(row.id);
            return true;
          });
        }

        employeeService
          .update(this.employee.id, this.employee)
          .then(() => {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                timer: 2000,
              })
              .fire({
                type: "success",
                title: "Employee updated successfully.",
              });
            this.$Progress.finish();
            this.close();
          })
          .catch((err) => {
            console.log(err);
            this.$swal
              .mixin({
                toast: true,
                showConfirmButton: true,
                position: "top-end",
                timer: 3000,
              })
              .fire({
                type: "error",
                title: err.data ? err.data.message : err.message,
              });
            this.$Progress.fail();
          });
      }
    },
    addLocation() {
      if (!this.selectedLocation) {
        return;
      }

      if (!this.selectedLocation.Location) {
        this.selectedLocation = {
          Location: this.selectedLocation,
          EmployeeRole: this.selectedRole,
        };
      }

      if (this.selectedLocation.delete) {
        this.selectedLocation.delete = false;
      } else {
        this.employee.ActiveLocations = this.employee.ActiveLocations.concat(
          this.selectedLocation
        );
      }

      const self = this;
      const foundIndex = this.locations.findIndex(function (element) {
        if (!self.selectedLocation.Location) {
          return self.selectedLocation.id === element.id;
        }
        return self.selectedLocation.Location.id === element.id;
      });

      if (foundIndex > -1) {
        this.locations.splice(foundIndex, 1);
        this.locations = [].concat(this.locations);
      }

      this.selectedLocation = 0;
      this.selectedRole = 0;
    },
    deleteLocation(index) {
      this.locations = [
        this.employee.ActiveLocations[index].Location,
        ...this.locations,
      ];
      this.employee.ActiveLocations[index].delete = true;
      this.employee.ActiveLocations = [].concat(this.employee.ActiveLocations);
    },
    cancel() {
      this.close();
    },
    close() {
      if (!this.isModal) {
        this.$router.go(-1);
      } else {
        this.$emit("close");
      }
    },
  },
  mixins: [validationMixin],
  validations: {
    employee: {
      pinCode: {
        required,
        minLength: minLength(4),
      },
      firstName: {
        required,
        minLength: minLength(2),
      },
      phoneNumber: {
        minLength: minLength(10),
      },
      email: {
        email,
      },
      Address: {
        addressLine1: {
          required: requiredIf(function (nestedModel) {
            return Object.keys(nestedModel).length > 0;
          }),
          minLength: minLength(2),
        },
        city: {
          required: requiredIf(function (nestedModel) {
            return Object.keys(nestedModel).length > 0;
          }),
        },
      },
    },
  },
};
</script>